import {
  collection, deleteDoc, doc, DocumentData, getDoc, getFirestore, limit, onSnapshot, orderBy, query,
  QueryConstraint, QuerySnapshot, serverTimestamp, setDoc, Timestamp
} from "@firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from "@firebase/storage";
import { useEffect, useState } from "react";
import app from "../firebase";

export type GalleryImageInfo = {
  id: string;
  storagePath: string;
  url: string;
  timestamp: Timestamp;
};

export async function uploadToGallery(img: File) {
  const dbRef = doc(collection(getFirestore(app), '/gallery'));

  const ext = img.type.substring(img.type.lastIndexOf('/') + 1);
  const storagePath = `/gallery/${ dbRef.id }.${ ext }`;

  await uploadBytes(ref(getStorage(app), storagePath), img);
  await setDoc(dbRef, {
    storagePath: storagePath,
    timestamp: serverTimestamp()
  });
}

export async function removeImgFromGallery(id: string) {
  const imgSnapshot = await getDoc(doc(getFirestore(app), `/gallery/${ id }`));
  await deleteObject(ref(getStorage(app), imgSnapshot.get('storagePath')));
  await deleteDoc(imgSnapshot.ref);
}

export async function getGalleryImgDownloadUrl(id: string) {
  const p = (await getDoc(doc(getFirestore(app), `/gallery/${ id }`))).get('storagePath');
  const url = (await getDownloadURL(ref(getStorage(app), p))).toString();
  return url;
}

export function useGallery(maxAmount?: number): GalleryImageInfo[] {
  const [ images, setImages ] = useState<GalleryImageInfo[]>([]);

  useEffect(() => {
    const c = collection(getFirestore(app), 'gallery');
    const qc: QueryConstraint[] = [
      orderBy('timestamp', 'desc'),
      !maxAmount ? null : limit(maxAmount)
    ].filter((q) => !!q) as QueryConstraint[];

    const unsub = onSnapshot(query(c, ...qc), (snapshot) => {
      getEntireGalleryDownloadUrls(snapshot).then((urls) => {
        setImages(snapshot.docs.map((d, i) => {
          return {
            id: d.id,
            storagePath: d.get('storagePath'),
            timestamp: d.get('timestamp'),
            url: urls[ i ]
          };
        }).sort((a, b) => {
          return (b.timestamp as Timestamp).seconds - (a.timestamp as Timestamp).seconds;
        }));
      });
    });

    return () => unsub();
  }, [ maxAmount ]);

  return images;
}

async function getEntireGalleryDownloadUrls(collectionSnapshot: QuerySnapshot<DocumentData>) {
  const storagePaths = collectionSnapshot.docs.map((d) => {
    return d.get('storagePath') as string;
  });

  const urls: string[] = [];
  const storage = getStorage(app);
  for (const p of storagePaths) {
    urls.push((await getDownloadURL(ref(storage, p))).toString());
  }

  return urls;
}
